import type { AgentHubGetOffersCommissionBody } from 'agentHub/api/agentHubCommission'
import { mapOfferToAgentHubCommissionPayload } from 'agentHub/api/mappers/commissionMap'
import { agentHubOrSubBrandConfig } from 'agentHub/constants'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import config from 'constants/config'
import type { LE_AGENT_HUB_HOTEL_BUNDLED_FLIGHT_ALLOWED_COUNTRIES } from 'constants/config/leagenthub'
import { AGENT_HUB_PAYMENT_MODE } from 'constants/payment'
import { groupBy } from 'lib/array/arrayUtils'
import { isBedbankOffer, isLpcHotelOffer } from 'lib/offer/offerTypes'
import { createSelector } from 'reselect'

export const isAgentHubEnabled = agentHubOrSubBrandConfig.isEnabled
export const isAgentHubCVFREnabled = agentHubOrSubBrandConfig.isCVFREnabled
export const isLuxLoyaltyEnabledInAgentHub = config.agentHub.isEnabled && config.LUX_LOYALTY_ENABLED

export function isSocieteEnabledForAccount(account: App.AgentHubAccount | undefined): boolean {
  if (!config.LUX_LOYALTY_IN_AGENT_HUB) return false
  if (!account) return false
  return account.societeMemberNumber !== undefined || account.societeRequestsInteractions !== undefined
}

export function shouldShowSocietePrompt(
  account: App.AgentHubAccount | undefined,
  interactionType: App.AgentHubSocietePromptInteractionType,
): boolean {
  if (!account) return false
  if (!isSocieteEnabledForAccount(account)) return false
  if (interactionType !== 'onboarding' && account.status !== 'onboarded') return false
  if (account.societeMemberNumber) return false

  const interactions = account.societeRequestsInteractions
  if (interactions?.engaged?.engagedAt) return false
  if (interactionType !== 'onboarding' && interactions?.[interactionType]?.deniedAt) return false

  if (interactionType === 'checkout' && !interactions?.login?.deniedAt) return false

  return true
}

/**
 * Determines if the flight deal banner should be shown for an offer.
 * For Agent Hub brands, only shows the banner for Maldives charter offers.
 * For non-Agent Hub brands, always returns true.
 */
export const shouldShowFlightDealBanner = (offer?: App.Offer): boolean => {
  if (!isAgentHubEnabled) {
    return true
  }

  const isMaldivesCharter = offer?.campaignTags?.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG) ?? false
  return isMaldivesCharter
}

const INDONESIA_COUNTRY_TOKEN = 'indonesia'
const INDONESIA_COUNTRY_CODE = 'ID'
const INDONESIA_ALLOWED_LOCATION_TOKENS = new Set(['bali'])

const AGENT_HUB_BUNDLED_FLIGHT_ALLOWED_LOCATIONS = [
  { country: 'singapore', countryCode: 'SG' },
  { country: 'vietnam', countryCode: 'VN' },
  { country: 'malaysia', countryCode: 'MY' },
  { country: 'maldives', countryCode: 'MV' },
  { country: 'new zealand', countryCode: 'NZ' },
  { country: 'hong kong', countryCode: 'HK' },
  { country: 'japan', countryCode: 'JP' },
] as const satisfies ReadonlyArray<{
  country: Exclude<(typeof LE_AGENT_HUB_HOTEL_BUNDLED_FLIGHT_ALLOWED_COUNTRIES)[number], typeof INDONESIA_COUNTRY_TOKEN>
  countryCode: string
}>

const AGENT_HUB_BUNDLED_FLIGHT_ALLOWED_COUNTRIES = new Set<string>(
  AGENT_HUB_BUNDLED_FLIGHT_ALLOWED_LOCATIONS.map(location => location.country),
)

const AGENT_HUB_BUNDLED_FLIGHT_ALLOWED_COUNTRY_CODES = new Set<string>(
  AGENT_HUB_BUNDLED_FLIGHT_ALLOWED_LOCATIONS.map(location => location.countryCode),
)

function normalizeLocationToken(value?: string): string | undefined {
  if (!value) {
    return undefined
  }

  return value
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim() || undefined
}

function splitLocationTokens(value?: string): Array<string> {
  if (!value) {
    return []
  }

  const splitValues = value
    .split(/[,/|]/)
    .flatMap(locationPart => {
      const normalizedLocationPart = normalizeLocationToken(locationPart)
      if (!normalizedLocationPart) {
        return []
      }

      return [normalizedLocationPart, ...normalizedLocationPart.split(/\s+/)]
    })
    .filter(Boolean)

  return Array.from(new Set(splitValues))
}

export function isAgentHubHotelBundledFlightsLocationWhitelisted(
  offer?: App.Offer,
): boolean {
  if (!offer) {
    return false
  }

  const countryCode = offer.property?.geoData?.countryCode?.toUpperCase()
  if (countryCode && AGENT_HUB_BUNDLED_FLIGHT_ALLOWED_COUNTRY_CODES.has(countryCode)) {
    return true
  }

  const locationCandidates = [
    offer.property?.geoData?.country,
    offer.locationSubheading,
    offer.locationHeading,
    offer.location,
  ]
    .flatMap(splitLocationTokens)

  if (locationCandidates.some(locationToken => AGENT_HUB_BUNDLED_FLIGHT_ALLOWED_COUNTRIES.has(locationToken))) {
    return true
  }

  const hasIndonesiaLocationAlias = locationCandidates.some(locationToken => INDONESIA_ALLOWED_LOCATION_TOKENS.has(locationToken))
  const hasIndonesiaSignal = countryCode === INDONESIA_COUNTRY_CODE ||
    locationCandidates.includes(INDONESIA_COUNTRY_TOKEN) ||
    hasIndonesiaLocationAlias
  if (hasIndonesiaSignal) {
    return hasIndonesiaLocationAlias
  }

  return false
}

export function isAgentHubHotelBundledFlightsAllowed(
  offer: App.Offer | undefined,
  canBookAllFlights: boolean,
): boolean {
  if (!isAgentHubEnabled) {
    return false
  }

  const isDirectMaldivesCharter = offer?.campaignTags?.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG) ?? false
  if (isDirectMaldivesCharter) {
    return true
  }

  if (!canBookAllFlights) {
    return false
  }

  const isWhitelisted = isAgentHubHotelBundledFlightsLocationWhitelisted(offer)
  return isWhitelisted
}

const getAgentHubOfferPackages = createSelector(
  (state: App.State, offerId: string) => state.offer.offers[offerId],
  (state: App.State, offerId: string) => state.offer.bedbankOffers[offerId],
  (state: App.State, offerId: string) => state.offer.bedbankOfferRates[offerId],
  (offer, bedbankOffer, offerRates) => {
    const rateKey = Object.keys(offerRates ?? {})[0]
    if (bedbankOffer || isBedbankOffer(offer)) {
      if (!offerRates?.[rateKey]) {
        return []
      }

      return offerRates[rateKey].map(rate => {
        return {
          packageId: rate.id,
          mx: rate.mx || '',
        }
      })
    }

    if (!offer?.packages) {
      return []
    }

    if (isLpcHotelOffer(offer)) {
      return offer.packages.filter(pkg => pkg.roomRate?.mx).map(pkg => {
        return {
          packageId: pkg.id,
          mx: pkg.roomRate?.mx ?? '',
        }
      })
    }

    return offer.packages.filter(pkg => pkg.mx).map(pkg => ({
      packageId: pkg.id,
      mx: pkg.mx || '',
    }))
  },
)

export const getAgentHubOfferCommissionBody = createSelector(
  (state: App.State, offerId: string) => getOfferById(state, offerId),
  getAgentHubOfferPackages,
  (offer, packages): AgentHubGetOffersCommissionBody | null => {
    if (!isAgentHubEnabled) {
      return null
    }

    if (!offer) {
      return null
    }

    const baseOfferBody = mapOfferToAgentHubCommissionPayload(offer)

    const offerBody = {
      ...baseOfferBody,
      packages,
    }

    return offerBody
  },
)

export const getAgentHubOfferCommissionBodyForBundledFlow = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State, flightWithJourney: App.FlightWithJourney) => flightWithJourney,
  (cartItems, flightWithJourney) => {
    const groupedCartItems = groupBy(cartItems, item => item.itemId, item => (item as App.Checkout.AccommodationItem).offerId)
    const bundledOffers = flightWithJourney.flight ? flightWithJourney.flight?.bundledItemIds?.filter(id => groupedCartItems.has(id)).flatMap(id => groupedCartItems.get(id) ?? []) : []
    return mapOfferToAgentHubCommissionPayload({ flight: flightWithJourney.flight, journey: flightWithJourney.journey }, { bundledOffers })
  },
)

export const getIsAgentHubGrossPayment = createSelector(
  (state: App.State) => state.checkout.payment.agentHubPaymentMode,
  (agentHubPaymentMode) => {
    return isAgentHubEnabled && agentHubPaymentMode === AGENT_HUB_PAYMENT_MODE.PAY_GROSS_AMOUNT
  },
)

const defaultAffiliationRules: Required<NonNullable<App.AgentHubAccount['affiliationRules']>> = {
  canBookCarHire: true,
  canBookCruise: true,
  canBookAllFlights: false,
}

export const getAgentHubAffiliationRules = createSelector(
  (state: App.State) => state.agentHub?.agentHubAccount?.account?.affiliationRules,
  (affiliationRules) => {
    return { ...defaultAffiliationRules, ...(affiliationRules ?? {}) } as Required<NonNullable<App.AgentHubAccount['affiliationRules']>>
  },
)

export const getAgentHubTier = (state: App.State) => state.agentHub.agentHubAccount.account?.agentTier

export const getCanAgentExportMedia = isAgentHubEnabled
export const isAgentHubAffiliationRequired = isAgentHubEnabled && !isAgentHubCVFREnabled

export const getAgentHubCharterAvailableDepartureAirports = (airports: Array<App.AirportLocation>) => {
  return airports.filter(airport => airport.airportCode === 'MEL')
}

export const getAgentHubCharterAvailableArrivalAirports = (airports: Array<App.AirportLocation>) => {
  return airports.filter(airport => airport.airportCode === 'MLE')
}

export const isAgentHubCharterAirport = (airport: App.AirportLocation) => {
  return airport.airportCode === 'MEL' || airport.airportCode === 'MLE'
}

export const getAgentHubCommissionForOffer = createSelector(
  (state: App.State, offerId?: string | undefined): App.AgentHubOfferCommission | null => (!isAgentHubEnabled || !offerId || state.agentHub.offersCommissionLoading[offerId] ? null : state.agentHub.offersCommissionMap[offerId]) ?? null,
  (commission) => commission,
)

export const getAgentHubCommissionMap = createSelector(
  (state: App.State) => state.agentHub.offersCommissionMap,
  (commissionMap) => isAgentHubEnabled ? commissionMap : {},
)

export const getOfferSummary = createSelector(
  (state: App.State, offerId: string) => state.offer.offerSummaries[offerId],
  (state: App.State, offerId: string) => state.offer.bedbankOfferSummaries[offerId],
  (state: App.State, offerId: string) => state.offer.tourV2Offers[offerId],
  (state: App.State, offerId: string) => state.offer.offers[`cruise:${offerId}`],
  (state: App.State, offerId: string) => state.cruise.cruiseOffers[`cruise-details:${offerId}`],
  (offerSummary, bedbankOfferSummary, tourV2OfferSummary, cruiseOfferSummary, cruiseDetails) => {
    return offerSummary ?? bedbankOfferSummary ?? tourV2OfferSummary ?? cruiseOfferSummary ?? cruiseDetails
  },
)

export const getOfferById = createSelector(
  (state: App.State, offerId: string) => state.offer.offers[offerId],
  (state: App.State, offerId: string) => state.offer.bedbankOffers[offerId],
  (state: App.State, offerId: string) => state.offer.tourV2Offers[offerId],
  (state: App.State, offerId: string) => state.offer.offers[`cruise:${offerId}`],
  (state: App.State, offerId: string) => state.cruise.cruiseOffers[`cruise-details:${offerId}`],
  (offer, bedbankOffer, tourV2Offer, cruiseOffer, cruiseDetails) => {
    return offer ?? bedbankOffer ?? tourV2Offer ?? cruiseOffer ?? cruiseDetails
  },
)
